import NoData from "@rio-cloud/rio-uikit/NoData";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";
import { TableDataProps } from ".";
import SharedTable from "../SharedTable";
import type { SharedTableBaseProps } from "../SharedTable";
import { useT } from "@hooks";

const SKELETON_ACTION_HEIGHT = 34;
const SKELETON_ACTION_WIDTH = 90;
const ACTION_HEAD_COUNT = 2;

type SortKey<T> = keyof T | [keyof T, keyof T];

export type Vehicle = {
  id: string;
  date: string;
  amount: string;
  branch: string;
  segment: string;
  customer: string;
  vehicle: string;
  driver: string[];
  category: string;
  subcategory: string;
  taxes: string;
};

interface Props extends SharedTableBaseProps<TableDataProps, SortKey<TableDataProps>> {
  onEdit?: (
    id: number,
    name: string,
    type?: string,
    expenseType?: string,
    categoryName?: string,
    categoryId?: number
  ) => void;
  onDelete?: (id: number, name: string) => void;
  onActivateOption?: (id: number) => void;
  onGetFile?: (id: string) => void;
  onDeleteFile?: (id: string) => void;
  onObservation?: (id: string) => void;
}

const Table = ({
  rows,
  isViewTypeTable,
  tableRef,
  tableClassNames,
  columns,
  columnLabels,
  columnsDetails,
  sortKey,
  sortDirection,
  handleSortChange,
  onActiveRowChange,
  onEdit,
  onDelete,
  onGetFile,
  onDeleteFile,
  onObservation,
  onActivateOption,
  isLoading = false,
}: Props) => {
  const t = useT();

  const esqueletoDeAcao = (
    <td className="width-100 width-150-sm">
      <ContentLoader className="display-block" height={SKELETON_ACTION_HEIGHT} width={SKELETON_ACTION_WIDTH} />
    </td>
  );

  return (
    <SharedTable<TableDataProps>
      rows={rows}
      isViewTypeTable={isViewTypeTable}
      tableRef={tableRef}
      tableClassNames={tableClassNames}
      columns={columns}
      columnsDetails={columnsDetails}
      columnLabels={columnLabels}
      sortBy={sortKey as string}
      sortDir={sortDirection}
      handleSortChange={handleSortChange}
      onActiveRowChange={onActiveRowChange}
      notFoundMessage={t("expense.table.notFound")}
      containerClassName="table-responsive min-height-300"
      isLoading={isLoading}
      actionHeadCount={ACTION_HEAD_COUNT}
      renderSkeletonActions={() => (
        <>
          {esqueletoDeAcao}
          {esqueletoDeAcao}
        </>
      )}
      getRowStyle={(row) => (!row?.active ? { backgroundColor: "var(--gray-lighter)" } : {})}
      renderCell={(row, col) => <span>{row[col as keyof TableDataProps] ?? <NoData text="-" />}</span>}
      renderRowActions={(row) => (
        <>
          {!row?.active ? (
            <>
              <td />
              <td className="width-100 width-150-sm">
                <button
                  onClick={() => onActivateOption?.(row.id)}
                  type="button"
                  className="btn btn-outline btn-primary border-none"
                >
                  <span className="rioglyph rioglyph-off" aria-hidden="true"></span>
                  {t("common.action.reactivate")}
                </button>
              </td>
            </>
          ) : null}

          {row?.active ? (
            <>
              <td className="width-100 width-150-sm">
                <button
                  onClick={() => {
                    onEdit?.(row.id, row.name, row?.type, row?.expenseType, row?.categoryName, row?.categoryId);
                  }}
                  type="button"
                  className="btn btn-default border-none"
                  disabled={row?.predefined}
                >
                  <span className="rioglyph rioglyph-pencil " aria-hidden="true"></span>
                  {t("common.action.edit")}
                </button>
              </td>
              <td className="width-100 width-150-sm">
                <button
                  onClick={() => onDelete?.(row.id, row.name)}
                  type="button"
                  className="btn btn-default border-none"
                  disabled={row?.predefined}
                >
                  <span className="rioglyph rioglyph-ban-circle " aria-hidden="true"></span>
                  {t("common.action.inactivate")}
                </button>
              </td>
            </>
          ) : null}
        </>
      )}
    />
  );
};

export default Table;
